/**
 * Checkout Component
 * 
 * Multi-step checkout flow with address validation, payment integration,
 * and order confirmation. Supports guest checkout and registered users.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useCartStore, cartSelectors } from '../../store/simpleCartStore';
import { wooCommerceProductsAPI } from '../../api/woocommerce/products';
import { Product, CartItem, Order, Address } from '../../types/ecommerce';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * Checkout step enum
 */
enum CheckoutStep {
  SHIPPING = 'shipping',
  PAYMENT = 'payment',
  REVIEW = 'review',
  CONFIRMATION = 'confirmation'
}

/**
 * Checkout props interface
 */
interface CheckoutProps {
  /** Custom CSS classes */
  className?: string;
  /** Callback when checkout is completed */
  onComplete?: (order: Order) => void;
  /** Callback when checkout is cancelled */
  onCancel?: () => void;
  /** Initial step to start from */
  initialStep?: CheckoutStep;
  /** Product data for cart items (optional - will fetch if not provided) */
  products?: Record<string, Product>;
}

/**
 * Shipping address form component
 */
interface ShippingFormProps {
  address: Address;
  onAddressChange: (address: Address) => void;
  onNext: () => void;
  onBack?: () => void;
  isValidating: boolean;
  errors: Record<string, string>;
}

const ShippingForm: React.FC<ShippingFormProps> = ({
  address,
  onAddressChange,
  onNext,
  onBack,
  isValidating,
  errors,
}) => {
  const [isSameAsBilling, setIsSameAsBilling] = useState(true);

  const handleFieldChange = (field: keyof Address, value: string) => {
    const updatedAddress = { ...address, [field]: value };
    onAddressChange(updatedAddress);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Shipping Address</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={address.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.firstName && (
                <p className="text-red-600 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={address.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.lastName && (
                <p className="text-red-600 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Address Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address Line 1 *
            </label>
            <input
              type="text"
              value={address.addressLine1}
              onChange={(e) => handleFieldChange('addressLine1', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.addressLine1 ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.addressLine1 && (
              <p className="text-red-600 text-xs mt-1">{errors.addressLine1}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address Line 2
            </label>
            <input
              type="text"
              value={address.addressLine2 || ''}
              onChange={(e) => handleFieldChange('addressLine2', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                value={address.city}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.city && (
                <p className="text-red-600 text-xs mt-1">{errors.city}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State/Province *
              </label>
              <input
                type="text"
                value={address.state}
                onChange={(e) => handleFieldChange('state', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.state ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.state && (
                <p className="text-red-600 text-xs mt-1">{errors.state}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP/Postal Code *
              </label>
              <input
                type="text"
                value={address.postalCode}
                onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.postalCode ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.postalCode && (
                <p className="text-red-600 text-xs mt-1">{errors.postalCode}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <select
              value={address.country}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.country ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Select Country</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="IT">Italy</option>
              <option value="ES">Spain</option>
              <option value="NL">Netherlands</option>
              <option value="SE">Sweden</option>
            </select>
            {errors.country && (
              <p className="text-red-600 text-xs mt-1">{errors.country}</p>
            )}
          </div>

          {/* Contact Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={address.phone || ''}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Billing Address Checkbox */}
          <div className="flex items-center">
            <input
              id="same-as-billing"
              type="checkbox"
              checked={isSameAsBilling}
              onChange={(e) => setIsSameAsBilling(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="same-as-billing" className="ml-2 block text-sm text-gray-700">
              Billing address is the same as shipping address
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            {onBack && (
              <Button
                type="button"
                onClick={onBack}
                variant="outline"
                size="lg"
              >
                Back
              </Button>
            )}
            
            <Button
              type="submit"
              disabled={isValidating}
              variant="primary"
              size="lg"
              className="ml-auto"
            >
              {isValidating ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  Validating...
                </div>
              ) : (
                'Continue to Payment'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Payment form component
 */
interface PaymentFormProps {
  onNext: () => void;
  onBack: () => void;
  isProcessing: boolean;
  paymentMethod: string;
  onPaymentMethodChange: (method: string) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  onNext,
  onBack,
  isProcessing,
  paymentMethod,
  onPaymentMethodChange,
}) => {
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h2>
        
        {/* Payment Method Selection */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center">
            <input
              id="card"
              type="radio"
              name="payment-method"
              value="card"
              checked={paymentMethod === 'card'}
              onChange={(e) => onPaymentMethodChange(e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <label htmlFor="card" className="ml-2 block text-sm text-gray-700">
              Credit/Debit Card
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              id="paypal"
              type="radio"
              name="payment-method"
              value="paypal"
              checked={paymentMethod === 'paypal'}
              onChange={(e) => onPaymentMethodChange(e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <label htmlFor="paypal" className="ml-2 block text-sm text-gray-700">
              PayPal
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              id="stripe"
              type="radio"
              name="payment-method"
              value="stripe"
              checked={paymentMethod === 'stripe'}
              onChange={(e) => onPaymentMethodChange(e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <label htmlFor="stripe" className="ml-2 block text-sm text-gray-700">
              Stripe
            </label>
          </div>
        </div>

        {/* Card Details Form */}
        {paymentMethod === 'card' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cardholder Name *
              </label>
              <input
                type="text"
                value={cardDetails.cardholderName}
                onChange={(e) => setCardDetails(prev => ({ ...prev, cardholderName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Number *
              </label>
              <input
                type="text"
                value={cardDetails.cardNumber}
                onChange={(e) => setCardDetails(prev => ({ ...prev, cardNumber: e.target.value }))}
                placeholder="1234 5678 9012 3456"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Date *
                </label>
                <input
                  type="text"
                  value={cardDetails.expiryDate}
                  onChange={(e) => setCardDetails(prev => ({ ...prev, expiryDate: e.target.value }))}
                  placeholder="MM/YY"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CVV *
                </label>
                <input
                  type="text"
                  value={cardDetails.cvv}
                  onChange={(e) => setCardDetails(prev => ({ ...prev, cvv: e.target.value }))}
                  placeholder="123"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </form>
        )}

        {/* PayPal Integration Placeholder */}
        {paymentMethod === 'paypal' && (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-gray-600 mb-4">You will be redirected to PayPal to complete your payment.</p>
            <div className="bg-blue-600 text-white px-4 py-2 rounded-md inline-block">
              PayPal Integration Placeholder
            </div>
          </div>
        )}

        {/* Stripe Integration Placeholder */}
        {paymentMethod === 'stripe' && (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-gray-600 mb-4">Secure payment processing with Stripe.</p>
            <div className="bg-purple-600 text-white px-4 py-2 rounded-md inline-block">
              Stripe Elements Integration Placeholder
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-6">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            size="lg"
          >
            Back to Shipping
          </Button>
          
          <Button
            type="button"
            onClick={onNext}
            disabled={isProcessing}
            variant="primary"
            size="lg"
          >
            {isProcessing ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Processing...
              </div>
            ) : (
              'Review Order'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Order review component
 */
interface OrderReviewProps {
  items: CartItem[];
  products: Record<string, Product>;
  shippingAddress: Address;
  paymentMethod: string;
  onPlaceOrder: () => void;
  onBack: () => void;
  isProcessing: boolean;
}

const OrderReview: React.FC<OrderReviewProps> = ({
  items,
  products,
  shippingAddress,
  paymentMethod,
  onPlaceOrder,
  onBack,
  isProcessing,
}) => {
  const calculateTotals = () => {
    let subtotal = 0;
    items.forEach(item => {
      const product = products[item.productId];
      if (product) {
        const price = item.variant?.price || product.price;
        subtotal += price * item.quantity;
      }
    });
    
    const tax = subtotal * 0.08; // 8% tax
    const shipping = subtotal > 50 ? 0 : 9.99;
    const total = subtotal + tax + shipping;
    
    return { subtotal, tax, shipping, total };
  };

  const { subtotal, tax, shipping, total } = calculateTotals();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Review Your Order</h2>
        
        {/* Order Items */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Order Items</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {items.map((item) => {
              const product = products[item.productId];
              if (!product) return null;

              const price = item.variant?.price || product.price;
              const itemTotal = price * item.quantity;

              return (
                <div key={`${item.productId}-${item.variant?.id || 'default'}`} className="p-4 flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12">
                    <img
                      src={product.images[0]?.src || '/placeholder-product.jpg'}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900">{product.name}</h4>
                    {item.variant && (
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.entries(item.variant.attributes).map(([key, value]) => 
                          `${key}: ${value}`
                        ).join(', ')}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    ${itemTotal.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Shipping Address */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Shipping Address</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-900">
              {shippingAddress.firstName} {shippingAddress.lastName}
            </p>
            <p className="text-sm text-gray-600">{shippingAddress.addressLine1}</p>
            {shippingAddress.addressLine2 && (
              <p className="text-sm text-gray-600">{shippingAddress.addressLine2}</p>
            )}
            <p className="text-sm text-gray-600">
              {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postalCode}
            </p>
            <p className="text-sm text-gray-600">{shippingAddress.country}</p>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Payment Method</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-900 capitalize">{paymentMethod}</p>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between font-medium">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            size="lg"
          >
            Back to Payment
          </Button>
          
          <Button
            type="button"
            onClick={onPlaceOrder}
            disabled={isProcessing}
            variant="primary"
            size="lg"
          >
            {isProcessing ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Placing Order...
              </div>
            ) : (
              `Place Order - $${total.toFixed(2)}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Order confirmation component
 */
interface OrderConfirmationProps {
  order: Order;
  onContinueShopping: () => void;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  order,
  onContinueShopping,
}) => {
  return (
    <div className="text-center space-y-6">
      <div className="text-green-600 text-6xl mb-4">✓</div>
      <h2 className="text-2xl font-bold text-gray-900">Order Confirmed!</h2>
      <p className="text-gray-600">
        Thank you for your order. Your order number is <strong>#{order.id}</strong>
      </p>
      
      <div className="bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto">
        <h3 className="font-medium text-gray-900 mb-2">Order Details</h3>
        <div className="space-y-1 text-sm text-gray-600">
          <p>Order ID: {order.id}</p>
          <p>Date: {order.dateCreated.toLocaleDateString()}</p>
          <p>Total: ${order.total.toFixed(2)}</p>
          <p>Status: {order.status}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          You will receive an email confirmation shortly.
        </p>
        
        <Button
          onClick={onContinueShopping}
          variant="primary"
          size="lg"
        >
          Continue Shopping
        </Button>
      </div>
    </div>
  );
};

/**
 * Main Checkout Component
 */
export const Checkout: React.FC<CheckoutProps> = ({
  className = '',
  onComplete,
  onCancel,
  initialStep = CheckoutStep.SHIPPING,
  products: propProducts,
}) => {
  const [currentStep, setCurrentStep] = useState<CheckoutStep>(initialStep);
  const [shippingAddress, setShippingAddress] = useState<Address>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [completedOrder, setCompletedOrder] = useState<Order | null>(null);

  // Cart store selectors
  const items = useCartStore(cartSelectors.items);
  const clearCart = useCartStore((state) => state.clearCart);

  /**
   * Load product details for cart items
   */
  useEffect(() => {
    // If products are passed as props, use them
    if (propProducts) {
      setProducts(propProducts);
      return;
    }

    // Otherwise, try to fetch from API
    const loadProducts = async () => {
      const productMap: Record<string, Product> = {};
      
      try {
        await Promise.all(
          items.map(async (item) => {
            const product = await wooCommerceProductsAPI.getProduct(item.productId);
            if (product) {
              productMap[item.productId] = product;
            }
          })
        );
        setProducts(productMap);
      } catch (error) {
        console.error('Failed to load product details:', error);
      }
    };

    if (items.length > 0) {
      loadProducts();
    }
  }, [items, propProducts]);

  /**
   * Validate shipping address
   */
  const validateShippingAddress = (address: Address): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!address.firstName.trim()) errors.firstName = 'First name is required';
    if (!address.lastName.trim()) errors.lastName = 'Last name is required';
    if (!address.addressLine1.trim()) errors.addressLine1 = 'Address is required';
    if (!address.city.trim()) errors.city = 'City is required';
    if (!address.state.trim()) errors.state = 'State is required';
    if (!address.postalCode.trim()) errors.postalCode = 'Postal code is required';
    if (!address.country.trim()) errors.country = 'Country is required';

    return errors;
  };

  /**
   * Handle shipping form submission
   */
  const handleShippingNext = useCallback(async () => {
    const errors = validateShippingAddress(shippingAddress);
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      
      // Simulate address validation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsLoading(false);
      setCurrentStep(CheckoutStep.PAYMENT);
    }
  }, [shippingAddress]);

  /**
   * Handle payment form submission
   */
  const handlePaymentNext = useCallback(() => {
    setCurrentStep(CheckoutStep.REVIEW);
  }, []);

  /**
   * Handle order placement
   */
  const handlePlaceOrder = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // Simulate order creation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const order: Order = {
        id: `ORDER-${Date.now()}`,
        customerId: 'guest',
        items: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.variant?.price || products[item.productId]?.price || 0,
          variant: item.variant,
        })),
        shippingAddress,
        billingAddress: shippingAddress, // Same as shipping for now
        paymentMethod,
        status: 'pending',
        total: items.reduce((sum, item) => {
          const product = products[item.productId];
          if (product) {
            const price = item.variant?.price || product.price;
            return sum + (price * item.quantity);
          }
          return sum;
        }, 0),
        subtotal: items.reduce((sum, item) => {
          const product = products[item.productId];
          if (product) {
            const price = item.variant?.price || product.price;
            return sum + (price * item.quantity);
          }
          return sum;
        }, 0),
        tax: 0,
        shipping: 0,
        dateCreated: new Date(),
        dateModified: new Date(),
      };

      setCompletedOrder(order);
      setCurrentStep(CheckoutStep.CONFIRMATION);
      
      // Clear cart
      await clearCart();
      
      // Notify parent component
      if (onComplete) {
        onComplete(order);
      }
    } catch (error) {
      console.error('Failed to place order:', error);
      // Handle error state
    } finally {
      setIsLoading(false);
    }
  }, [items, products, shippingAddress, paymentMethod, clearCart, onComplete]);

  /**
   * Handle step navigation
   */
  const handleBack = useCallback(() => {
    switch (currentStep) {
      case CheckoutStep.PAYMENT:
        setCurrentStep(CheckoutStep.SHIPPING);
        break;
      case CheckoutStep.REVIEW:
        setCurrentStep(CheckoutStep.PAYMENT);
        break;
      default:
        if (onCancel) {
          onCancel();
        }
        break;
    }
  }, [currentStep, onCancel]);

  // Step indicator
  const steps = [
    { key: CheckoutStep.SHIPPING, label: 'Shipping', completed: currentStep !== CheckoutStep.SHIPPING },
    { key: CheckoutStep.PAYMENT, label: 'Payment', completed: [CheckoutStep.REVIEW, CheckoutStep.CONFIRMATION].includes(currentStep) },
    { key: CheckoutStep.REVIEW, label: 'Review', completed: currentStep === CheckoutStep.CONFIRMATION },
    { key: CheckoutStep.CONFIRMATION, label: 'Confirmation', completed: false },
  ];

  return (
    <div className={`max-w-4xl mx-auto p-4 ${className}`}>
      {/* Progress Indicator */}
      {currentStep !== CheckoutStep.CONFIRMATION && (
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === step.key
                      ? 'bg-blue-600 text-white'
                      : step.completed
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step.completed ? '✓' : index + 1}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    currentStep === step.key ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    step.completed ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {currentStep === CheckoutStep.SHIPPING && (
          <ShippingForm
            address={shippingAddress}
            onAddressChange={setShippingAddress}
            onNext={handleShippingNext}
            onBack={onCancel}
            isValidating={isLoading}
            errors={validationErrors}
          />
        )}

        {currentStep === CheckoutStep.PAYMENT && (
          <PaymentForm
            onNext={handlePaymentNext}
            onBack={handleBack}
            isProcessing={isLoading}
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
          />
        )}

        {currentStep === CheckoutStep.REVIEW && (
          <OrderReview
            items={items}
            products={products}
            shippingAddress={shippingAddress}
            paymentMethod={paymentMethod}
            onPlaceOrder={handlePlaceOrder}
            onBack={handleBack}
            isProcessing={isLoading}
          />
        )}

        {currentStep === CheckoutStep.CONFIRMATION && completedOrder && (
          <OrderConfirmation
            order={completedOrder}
            onContinueShopping={() => {
              if (onCancel) {
                onCancel();
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Checkout;